import { useCallback, useMemo, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DataTable, type DataFetchParams } from '@/components/DataTable';
import type { DeviceListItem } from '@kirin/api-client/contracts/device-inventory';
import { queryKeys } from '@/hooks/api/queryKeys';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useTimestampFormat } from '@/hooks/useTimestampFormat';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import {
  fetchDevicePosture,
  fetchDevicesDataTable,
  type DeviceTableMeta,
} from './api';
import { createDeviceColumns } from './columns';
import { DEVICES_TABLE_CONFIG } from './constants';
import { DevicePostureTiles } from './DevicePostureTiles';
import { buildEndpointPagePath } from './links';

/**
 * KIR-5412 (Mockup 1) — Inventory → Endpoints: posture tiles over one row per
 * physical machine. Opening a row shows its endpoint page. Rendered only while
 * `shared_devices` is on (the tab's gate); read-only for every role.
 */
export function DataTableEndpointsContent({
  organizationId,
}: {
  organizationId: string;
}): ReactElement {
  const navigate = useNavigate();
  const { value: timestampFormat } = useTimestampFormat();
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.AGENT_AUTO_UPDATE,
    flagsmithFeatureFlags.ENDPOINT_IDENTIFIER,
  ]);
  const showUpdateState =
    flags[flagsmithFeatureFlags.AGENT_AUTO_UPDATE] ?? false;
  // The Alerts page drops the endpoint filter without endpoint_identifier, so
  // an endpoint-scoped link would open every alert in the org.
  const linkToAlerts =
    flags[flagsmithFeatureFlags.ENDPOINT_IDENTIFIER] ?? false;
  const [latestAgentVersion, setLatestAgentVersion] = useState<string | null>(
    null
  );
  const onMeta = useCallback(
    (meta: DeviceTableMeta) => setLatestAgentVersion(meta.latestAgentVersion),
    []
  );

  const posture = useQuery({
    queryKey: queryKeys.devices.posture(organizationId),
    queryFn: () => fetchDevicePosture(organizationId),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const columns = useMemo(
    () =>
      createDeviceColumns(organizationId, timestampFormat, {
        showUpdateState,
        latestAgentVersion,
        linkToAlerts,
      }),
    [
      organizationId,
      timestampFormat,
      showUpdateState,
      latestAgentVersion,
      linkToAlerts,
    ]
  );

  const getQueryKeysFromParams = useCallback(
    (params: DataFetchParams) =>
      queryKeys.devices.dataTable(organizationId, {
        page: params.pagination.pageIndex + 1,
        limit: params.pagination.pageSize,
        filters: params.filters,
        sortBy: params.sort?.field,
        sortOrder: params.sort?.order,
        search: params.search,
      }),
    [organizationId]
  );

  const openDevice = useCallback(
    (row: DeviceListItem) =>
      navigate(buildEndpointPagePath(organizationId, row.id)),
    [navigate, organizationId]
  );

  const queryConfig = useMemo(
    () => ({ staleTime: 30_000, refetchOnWindowFocus: false }),
    []
  );
  const searchConfig = useMemo(
    () => ({
      placeholder: 'Search endpoints by name or ID',
      className: 'w-[280px]',
    }),
    []
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <DevicePostureTiles
        posture={posture.data}
        isLoading={posture.isLoading}
        isError={posture.isError}
        showUpdateStates={showUpdateState}
      />
      <DataTable<DeviceListItem, DeviceTableMeta>
        id={DEVICES_TABLE_CONFIG.tableId}
        mode="server"
        className="flex flex-col flex-1 min-h-0"
        containerClassName="flex-1"
        tableLayout="auto"
        columns={columns}
        errorBoundaryLabel="Something went wrong in the endpoints table"
        search={searchConfig}
        features={{ enableSorting: true }}
        onRowClick={openDevice}
        getRowClassName={() => 'cursor-pointer'}
        queryFn={(params, signal) =>
          fetchDevicesDataTable(organizationId, params, signal)
        }
        getQueryKeysFromParams={getQueryKeysFromParams}
        onMeta={onMeta}
        noDataLabel="No endpoints found."
        queryConfig={queryConfig}
      />
    </div>
  );
}

DataTableEndpointsContent.displayName = 'DataTableEndpointsContent';
